import { Check } from 'lucide-react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { CalendarEventSource } from '../../../types/calendar';
import { getEventAppearance } from '../utils/calendarEventAppearance';
import {
  ALL_EVENT_SOURCES,
  type EventFilter,
  type EventSourceCounts,
  toggleSource,
} from '../utils/calendarEventFilter';

interface CalendarSourceFilterProps {
  filter: EventFilter;
  /** Counted before filtering, so a rep can see what turning a source on would show. */
  counts: EventSourceCounts;
  onChange: (filter: EventFilter) => void;
}

const SOURCE_LABEL_KEYS: Record<CalendarEventSource, string> = {
  manual: 'calendar.eventSource.manual.label',
  voice_agent: 'calendar.eventSource.voiceAgent.label',
  platform_agent: 'calendar.eventSource.platformAgent.label',
  provider_sync: 'calendar.eventSource.providerSync.label',
};

/**
 * "Show me just mine" — a board carrying voice-agent and platform-agent
 * bookings alongside a rep's own meetings needs one.
 *
 * Every row is a real checkbox: on/off may not ride on a tint, because the
 * selected fill against the unselected track measures around 1.4:1, far under
 * the 3:1 WCAG 1.4.11 asks of a UI state boundary. The colour swatch beside
 * each label is the source's grid hue, so the filter doubles as the legend.
 */
export function CalendarSourceFilter({
  filter,
  counts,
  onChange,
}: Readonly<CalendarSourceFilterProps>) {
  const { t } = useTranslation();

  return (
    <VStack spacing="xxs" testID="calendar_source_filter">
      {ALL_EVENT_SOURCES.map((source) => {
        const checked = filter.sources.has(source);
        const label = t(SOURCE_LABEL_KEYS[source]);

        return (
          <Pressable
            key={source}
            testID={`calendar_source_filter_${source}`}
            accessibilityRole="checkbox"
            accessibilityState={{ checked }}
            accessibilityLabel={label}
            onPress={() => onChange(toggleSource(filter, source))}
            style={styles.row}
          >
            <HStack spacing="sm" align="center" style={styles.rowInner}>
              <CheckBox checked={checked} />
              <View
                style={[
                  styles.swatch,
                  { backgroundColor: getEventAppearance(source, 'scheduled').borderColor },
                ]}
              />
              <Text variant="bodySmall" color={colors.text.primary} style={styles.label}>
                {label}
              </Text>
              <Text variant="caption" color={colors.text.secondary} style={styles.count}>
                {counts[source]}
              </Text>
            </HStack>
          </Pressable>
        );
      })}

      <Pressable
        testID="calendar_filter_hide_cancelled"
        accessibilityRole="checkbox"
        accessibilityState={{ checked: filter.hideCancelled }}
        accessibilityLabel={t('calendar.filter.hideCancelled')}
        onPress={() => onChange({ ...filter, hideCancelled: !filter.hideCancelled })}
        style={styles.row}
      >
        <HStack spacing="sm" align="center" style={styles.rowInner}>
          <CheckBox checked={filter.hideCancelled} />
          <Text variant="bodySmall" color={colors.text.primary} style={styles.label}>
            {t('calendar.filter.hideCancelled')}
          </Text>
        </HStack>
      </Pressable>
    </VStack>
  );
}

// A real filled box with a tick, not a tinted row: the glyph is what carries
// the state where the fill cannot.
function CheckBox({ checked }: Readonly<{ checked: boolean }>) {
  return (
    <Box
      style={[
        styles.checkbox,
        checked
          ? { backgroundColor: colors.primary[500], borderColor: colors.primary[500] }
          : { borderColor: colors.border.strong },
      ]}
      alignItems="center"
      justifyContent="center"
    >
      {checked && <Check size={12} color={colors.text.inverse} strokeWidth={3} />}
    </Box>
  );
}

const styles = StyleSheet.create({
  row: {
    minHeight: tokens.interaction.minimumTarget.default,
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.xs,
    borderRadius: tokens.radius.md,
  },
  rowInner: {
    flex: 1,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: tokens.radius.sm,
    borderWidth: 1.5,
  },
  swatch: {
    width: 8,
    height: 8,
    borderRadius: tokens.radius.full,
  },
  label: {
    flex: 1,
  },
  count: {
    fontVariant: ['tabular-nums'],
  },
});
